/**
 * `.zxbak` 外层容器：
 *
 *   magic(8) | version(1) | salt(16) | iv(12) | AES-256-GCM ciphertext+tag
 *
 * 密钥派生：PBKDF2-SHA256，600,000 次，输出 256-bit AES-GCM key。
 * 头部（magic/version/salt/iv）作为 AES-GCM 的 additionalData，任何头部篡改都会解密失败。
 * 密码只存在于函数调用栈内，不写入 localStorage / IndexedDB / 文件。
 */
import { concatBytes, equalBytes, utf8 } from './codec';
import {
  BACKUP_MAGIC,
  BackupError,
  IV_BYTES,
  PBKDF2_ITERATIONS,
  SALT_BYTES,
} from './types';

export const CONTAINER_VERSION = 1;
export const CONTAINER_HEADER_BYTES = 8 + 1 + SALT_BYTES + IV_BYTES;

const MAGIC_BYTES = utf8(BACKUP_MAGIC);
const GCM_TAG_BYTES = 16;

export interface ContainerHeader {
  version: number;
  salt: Uint8Array;
  iv: Uint8Array;
  bytes: Uint8Array;
}

function defaultRandomBytes(length: number): Uint8Array {
  if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
    throw new BackupError('FORMAT', '当前环境不支持安全随机数，无法创建加密备份');
  }
  return crypto.getRandomValues(new Uint8Array(length));
}

function buildHeader(
  salt: Uint8Array,
  iv: Uint8Array,
  version = CONTAINER_VERSION,
): Uint8Array {
  const header = new Uint8Array(CONTAINER_HEADER_BYTES);
  header.set(MAGIC_BYTES, 0);
  header[8] = version;
  header.set(salt, 9);
  header.set(iv, 9 + SALT_BYTES);
  return header;
}

/** 判断一段字节是否以 `.zxbak` 魔数开头（用于区分旧 JSON 导入路径）。 */
export function isEncryptedBackup(data: Uint8Array): boolean {
  return data.length >= CONTAINER_HEADER_BYTES && equalBytes(data.subarray(0, 8), MAGIC_BYTES);
}

/** 读取头部；不校验密码（仅做结构与范围校验）。 */
export function readContainerHeader(data: Uint8Array): ContainerHeader {
  if (!isEncryptedBackup(data)) {
    throw new BackupError('FORMAT', '不是有效的加密备份文件（魔数不匹配）');
  }
  const version = data[8] ?? 0;
  if (version !== CONTAINER_VERSION) {
    throw new BackupError('FORMAT', `不支持的备份容器版本：${version}`);
  }
  if (data.length < CONTAINER_HEADER_BYTES + GCM_TAG_BYTES) {
    throw new BackupError('FORMAT', '备份文件不完整');
  }
  const salt = data.slice(9, 9 + SALT_BYTES);
  const iv = data.slice(9 + SALT_BYTES, CONTAINER_HEADER_BYTES);
  return { version, salt, iv, bytes: data.slice(0, CONTAINER_HEADER_BYTES) };
}

async function deriveKey(salt: Uint8Array, password: string): Promise<CryptoKey> {
  if (typeof crypto === 'undefined' || typeof crypto.subtle === 'undefined') {
    throw new BackupError('FORMAT', '当前环境不支持 WebCrypto，无法使用加密备份');
  }
  const base = await crypto.subtle.importKey('raw', utf8(password), 'PBKDF2', false, [
    'deriveKey',
  ]);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** AES-256-GCM 加密已打包的 ZIP 字节，返回完整 `.zxbak` 字节。 */
export async function encryptContainer(
  zip: Uint8Array,
  password: string,
  randomBytes: (length: number) => Uint8Array = defaultRandomBytes,
): Promise<Uint8Array> {
  if (password === '') throw new BackupError('PASSWORD', '备份密码不能为空');
  const salt = randomBytes(SALT_BYTES);
  const iv = randomBytes(IV_BYTES);
  if (salt.length !== SALT_BYTES || iv.length !== IV_BYTES) {
    throw new BackupError('FORMAT', '随机盐 / IV 长度不正确');
  }
  const header = buildHeader(salt, iv);
  const key = await deriveKey(salt, password);
  const cipher = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: header },
    key,
    zip,
  );
  return concatBytes(header, new Uint8Array(cipher));
}

/** 解密 `.zxbak` 并返回 ZIP 明文；密码错误 / 文件被篡改统一报「密码错误或已损坏」。 */
export async function decryptContainer(
  data: Uint8Array,
  password: string,
): Promise<Uint8Array> {
  const header = readContainerHeader(data);
  if (password === '') throw new BackupError('PASSWORD', '请输入备份密码');
  const key = await deriveKey(header.salt, password);
  try {
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: header.iv, additionalData: header.bytes },
      key,
      data.subarray(CONTAINER_HEADER_BYTES),
    );
    return new Uint8Array(plain);
  } catch {
    throw new BackupError('PASSWORD', '密码错误，或备份文件已损坏');
  }
}
